import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, CreditCard, ExternalLink, AlertCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

export default function AccountBilling() {
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const apiUrl = process.env.NODE_ENV === 'development'
    ? 'http://localhost:3001'
    : '';

  const handleManageBilling = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${apiUrl}/api/create-portal-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to open billing portal');
      }

      // Redirect to Stripe Customer Portal
      window.location.href = data.url;
    } catch (err) {
      console.error('Billing portal error:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  const getPlanName = (plan) => {
    const plans = {
      institutional: 'HDALGOS INSTITUTIONAL SUITE',
      custom: 'CUSTOM INDICATOR',
      // Legacy plans (for existing customers)
      core: 'CORE (Legacy)',
      pro: 'PRO (Legacy)',
      elite: 'ELITE (Legacy)',
    };
    return plans[plan] || plan?.toUpperCase() || 'Unknown';
  };

  const getPlanPrice = (plan, billingCycle) => {
    const prices = {
      institutional: { monthly: 49, yearly: 480 },
      custom: { monthly: 0, yearly: 0 }, // Custom pricing
      // Legacy plans (for existing customers)
      core: { monthly: 39, yearly: 376 },
      pro: { monthly: 59, yearly: 570 },
      elite: { monthly: 150, yearly: 1440 },
    };

    const planPrices = prices[plan] || { monthly: 0, yearly: 0 };
    return billingCycle === 'yearly' ? planPrices.yearly : planPrices.monthly;
  };

  if (!user.subscription) {
    return (
      <div className="min-h-screen bg-[#111315] text-white">
        {/* Header */}
        <nav className="border-b border-neutral-800 bg-[#111315]/95 backdrop-blur-xl">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="flex justify-between items-center h-20">
              <div className="flex items-center">
                <img src="/logo.png" alt="HDAlgos logo" className="h-10 w-auto" />
                <span className="sr-only">HDAlgos Institutional Systems</span>
              </div>
              <Link to="/account" className="text-sm text-neutral-400 hover:text-white transition flex items-center gap-2">
                <ArrowLeft className="w-4 h-4" />
                Back to Dashboard
              </Link>
            </div>
          </div>
        </nav>

        <div className="max-w-3xl mx-auto px-6 py-16">
          <div className="bg-neutral-950 ring-1 ring-neutral-800 rounded-xl p-8 text-center">
            <AlertCircle className="w-16 h-16 text-neutral-600 mx-auto mb-4" />
            <h2 className="text-2xl font-black mb-2">No Active Subscription</h2>
            <p className="text-neutral-400 mb-6">
              You don't have an active subscription. Choose a plan to get started.
            </p>
            <Link
              to="/#pricing"
              className="inline-block bg-gradient-to-r from-[#CBB26A] to-[#A8914D] text-black px-8 py-3 rounded-lg font-bold hover:from-[#D8C585] hover:to-[#BDA45E] transition"
            >
              View Plans
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#111315] text-white">
      {/* Header */}
      <nav className="border-b border-neutral-800 bg-[#111315]/95 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center">
              <img src="/logo.png" alt="HDAlgos logo" className="h-10 w-auto" />
              <span className="sr-only">HDAlgos Institutional Systems</span>
            </div>
            <Link to="/account" className="text-sm text-neutral-400 hover:text-white transition flex items-center gap-2">
              <ArrowLeft className="w-4 h-4" />
              Back to Dashboard
            </Link>
          </div>
        </div>
      </nav>

      <div className="max-w-3xl mx-auto px-6 py-16">
        {/* Header */}
        <div className="mb-12">
          <h1 className="text-4xl md:text-5xl font-black mb-2">Billing & Plans</h1>
          <p className="text-neutral-400">Manage your subscription and payment methods</p>
        </div>

        {error && (
          <div className="bg-red-950/30 ring-1 ring-red-900/50 rounded-lg p-4 mb-6">
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}

        <div className="space-y-8">
          {/* Current Plan */}
          <div className="bg-neutral-950 ring-1 ring-neutral-800 rounded-xl p-8">
            <h2 className="text-2xl font-black mb-6">Current Plan</h2>

            <div className="bg-gradient-to-br from-[#1f1f20]/90 via-[#18191b]/75 to-[#121315]/80 ring-2 ring-[#CBB26A]/50 rounded-xl p-6 mb-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <div className="text-3xl font-black text-[#CBB26A] mb-1">
                    {getPlanName(user.subscription.plan)}
                  </div>
                  <div className="text-sm text-neutral-400 capitalize">
                    {user.subscription.billingCycle} billing
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-black">
                    ${getPlanPrice(user.subscription.plan, user.subscription.billingCycle)}
                  </div>
                  <div className="text-xs text-neutral-400">
                    /{user.subscription.billingCycle === 'yearly' ? 'year' : 'month'}
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t border-neutral-800">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-neutral-400">Status</span>
                  <span className={`font-bold capitalize ${user.subscription.status === 'trialing' ? 'text-green-400' : user.subscription.status === 'active' ? 'text-green-400' : 'text-neutral-400'}`}>
                    {user.subscription.status === 'trialing' ? 'Free Trial' : user.subscription.status}
                  </span>
                </div>
                {user.subscription.currentPeriodEnd && (
                  <div className="flex justify-between text-sm">
                    <span className="text-neutral-400">
                      {user.subscription.status === 'trialing'
                        ? 'Trial Ends'
                        : user.subscription.cancelAtPeriodEnd
                        ? 'Expires'
                        : 'Renews'}
                    </span>
                    <span className="font-bold">
                      {new Date(user.subscription.currentPeriodEnd).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                      })}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {user.subscription.status === 'trialing' && (
              <div className="bg-green-500/10 ring-1 ring-green-500/30 rounded-lg p-4 mb-6">
                <p className="text-green-400 text-sm font-semibold mb-1">
                  ✓ You're on a free trial
                </p>
                <p className="text-neutral-400 text-xs">
                  Your trial ends on {new Date(user.subscription.currentPeriodEnd).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}.
                  You won't be charged until then. Cancel anytime before the trial ends to avoid being charged.
                </p>
              </div>
            )}

            {user.subscription.cancelAtPeriodEnd && (
              <div className="bg-yellow-950/30 ring-1 ring-yellow-900/50 rounded-lg p-4 mb-6">
                <p className="text-yellow-400 text-sm">
                  Your subscription will be canceled at the end of the current billing period. You can reactivate it anytime before then through the billing portal.
                </p>
              </div>
            )}

            <button
              onClick={handleManageBilling}
              disabled={loading}
              className="w-full bg-gradient-to-r from-[#CBB26A] to-[#A8914D] text-black py-4 rounded-lg font-bold hover:from-[#D8C585] hover:to-[#BDA45E] transition flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <CreditCard className="w-5 h-5" />
              {loading ? 'Opening Portal...' : 'Manage Billing in Stripe'}
              <ExternalLink className="w-4 h-4" />
            </button>

            <p className="text-xs text-neutral-500 text-center mt-4">
              You'll be redirected to Stripe's secure billing portal where you can update payment methods, view invoices, change plans, or cancel your subscription.
            </p>
          </div>

          {/* What You Can Do */}
          <div className="bg-neutral-950 ring-1 ring-neutral-800 rounded-xl p-8">
            <h2 className="text-2xl font-black mb-6">Manage Your Subscription</h2>

            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 rounded-full bg-[#CBB26A] mt-2 flex-shrink-0"></div>
                <div>
                  <div className="font-bold mb-1">Update Payment Method</div>
                  <div className="text-sm text-neutral-400">
                    Add or update your credit card information
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-2 h-2 rounded-full bg-[#CBB26A] mt-2 flex-shrink-0"></div>
                <div>
                  <div className="font-bold mb-1">View Invoices</div>
                  <div className="text-sm text-neutral-400">
                    Download past invoices and payment history
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-2 h-2 rounded-full bg-[#CBB26A] mt-2 flex-shrink-0"></div>
                <div>
                  <div className="font-bold mb-1">Switch Billing Cycle</div>
                  <div className="text-sm text-neutral-400">
                    Change between monthly and yearly billing
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-2 h-2 rounded-full bg-[#CBB26A] mt-2 flex-shrink-0"></div>
                <div>
                  <div className="font-bold mb-1">Cancel Subscription</div>
                  <div className="text-sm text-neutral-400">
                    Cancel anytime - you'll retain access until the end of your billing period
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
